import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
// @ts-ignore CSS is loaded by the application bundler.
import "../AgGridTheme.css";
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ModuleRegistry,
  PaginationModule,
  RowApiModule,
  RowStyleModule,
  ScrollApiModule,
} from "ag-grid-community";
import { FaCubes, FaGlobe } from "react-icons/fa";
import { FiPackage } from "react-icons/fi";
import SearchBar from "../../SearchBarTable/SearchBarTable";
import SortHeader from "../SortingHeader";

// CellStyle for cellStyle, RowStyle for getRowClass, RowApi for redrawRows and
// forEachNode, ScrollApi for ensureNodeVisible. The app registers
// AllCommunityModule from the FileCenter page; don't depend on it.
ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelModule,
  PaginationModule,
  RowApiModule,
  RowStyleModule,
  ScrollApiModule,
]);

export interface OnboardingAssetRow {
  asset_id: number;
  asset_name: string;
  generation_subtype_code?: string | null;
  iso_code?: string | null;
}

interface Props {
  assets: OnboardingAssetRow[];
  selectedId: number | null;
  onRowSelect: (asset: OnboardingAssetRow) => void;
}

const headerClass =
  "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer";

const col = (field: string, headerName: string, icon: React.ReactNode) => ({
  headerName,
  field,
  sortable: true,
  flex: 1,
  minWidth: 140,
  headerComponent: SortHeader,
  headerComponentParams: { displayName: headerName, enableSorting: true, icon },
  headerClass,
});

/** Asset list on the left of the Onboarding tab (same look as Asset Setup's table). */
const OnboardingAssetTable: React.FC<Props> = ({ assets, selectedId, onRowSelect }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef<AgGridReact>(null);

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      [a.asset_name, a.generation_subtype_code, a.iso_code].some((v) =>
        (v || "").toLowerCase().includes(q),
      ),
    );
  }, [assets, searchQuery]);

  const columnDefs = useMemo(
    () => [
      { ...col("asset_name", "Asset", <FaCubes className="text-gray-600" />), minWidth: 200 },
      col("generation_subtype_code", "Asset Type", <FiPackage className="text-gray-600" />),
      col("iso_code", "ISO", <FaGlobe className="text-gray-600" />),
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      cellStyle: {
        padding: "8px 12px",
        fontSize: "16px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
      },
    }),
    [],
  );

  // getRowClass is only read when a row is drawn. Also bring the picked row into
  // view: a just-created asset is appended last, so first go to the page that
  // holds it (ensureNodeVisible only scrolls within the current page).
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.redrawRows();
    api.forEachNode((node) => {
      if (node.data?.asset_id !== selectedId) return;
      if (node.rowIndex !== null) {
        api.paginationGoToPage(Math.floor(node.rowIndex / api.paginationGetPageSize()));
      }
      api.ensureNodeVisible(node);
    });
  }, [selectedId, assets]);

  return (
    <div className="p-4 bg-white shadow-md rounded-xl font-poppins">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Search assets..."
          />
        </div>
      </div>
      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700"
        style={{
          height: "var(--admin-tab-grid-height, max(475px, calc(100dvh - 320px)))",
          width: "100%",
          overflow: "auto",
          border: "1px solid #E7E4FF",
          borderRadius: "8px",
        }}
      >
        <AgGridReact
          ref={gridRef}
          theme="legacy"
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="normal"
          rowHeight={60}
          headerHeight={55}
          pagination={true}
          paginationPageSize={50}
          onRowClicked={(event) => event.data && onRowSelect(event.data)}
          getRowClass={(params) =>
            params.data?.asset_id === selectedId ? "admin-row-selected" : ""
          }
        />
      </div>
    </div>
  );
};

export default OnboardingAssetTable;
